import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useOptionalLifeOS } from './LifeOSProvider';
import {
  lifeOSClassNames,
  lifeOSDateTime,
  lifeOSItemExcerpt,
  lifeOSItemTitle,
  lifeOSTypeLabel,
} from './presentation';
import './life-os.css';

const DEFAULT_FILTERS = [
  { id: '', label: 'Tudo' },
  { id: 'file', label: 'Arquivos' },
  { id: 'note', label: 'Notas' },
  { id: 'idea', label: 'Ideias' },
  { id: 'link', label: 'Links' },
];
const EMPTY_VAULT_STATE = Object.freeze({});

function vaultItems(data, state) {
  if (Array.isArray(state?.items)) return state.items;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.vault)) return data.vault;
  return [];
}

function resultItems(data, state) {
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(state?.results)) return state.results;
  return [];
}

function itemType(item) {
  return item?.item_type || item?.type || item?.kind || item?.category || '';
}

function itemTags(item) {
  if (Array.isArray(item?.tags)) return item.tags.filter(Boolean).slice(0, 3);
  if (Array.isArray(item?.metadata?.tags)) return item.metadata.tags.filter(Boolean).slice(0, 3);
  return [];
}

function VaultLoading() {
  return (
    <div className="life-os-vault-grid" role="status" aria-label="Carregando Vault">
      {[0, 1, 2, 3].map(index => <div className="life-os-skeleton life-os-skeleton--vault" key={index} aria-hidden="true" />)}
      <span className="life-os-sr-only">Carregando Vault…</span>
    </div>
  );
}

function VaultCard({ item, onOpenItem }) {
  const title = lifeOSItemTitle(item) || lifeOSTypeLabel(itemType(item)) || 'Item salvo';
  const excerpt = lifeOSItemExcerpt(item);
  const tags = itemTags(item);
  const type = lifeOSTypeLabel(itemType(item));
  const updatedAt = item?.updated_at || item?.saved_at || item?.created_at;
  const cardContent = (
    <>
      <div className="life-os-vault-card__topline">
        {type && <span className="life-os-kind">{type}</span>}
        {updatedAt && <time>{lifeOSDateTime(updatedAt)}</time>}
      </div>
      <h3>{title}</h3>
      {excerpt && <p>{excerpt}</p>}
      {(tags.length > 0 || item?.source) && (
        <div className="life-os-vault-card__tags">
          {tags.map(tag => <span key={tag}>#{tag}</span>)}
          {item?.source && <span>{String(item.source)}</span>}
        </div>
      )}
    </>
  );
  if (onOpenItem) {
    return (
      <li>
        <button type="button" className="life-os-vault-card life-os-vault-card--action" onClick={() => onOpenItem(item)} aria-label={`Abrir ${title}`}>
          {cardContent}
        </button>
      </li>
    );
  }
  return <li className="life-os-vault-card">{cardContent}</li>;
}

/**
 * Mobile-first Vault browser. `globalSearch` switches only the search action
 * to Life OS global search; normal browsing remains the typed Vault endpoint.
 */
export function VaultPanel({
  data,
  state,
  autoLoad = true,
  className,
  title = 'Vault',
  type,
  category,
  projectId,
  filters = DEFAULT_FILTERS,
  onFilterChange,
  globalSearch = false,
  initialQuery = '',
  maxVisibleItems = 24,
  onRefresh,
  onSearch,
  onLoadMore,
  onOpenItem,
  onAddItem,
  emptyMessage = 'Nada foi salvo aqui ainda.',
}) {
  const panelId = useId();
  const lifeOS = useOptionalLifeOS();
  const [query, setQuery] = useState(initialQuery);
  const [selectedType, setSelectedType] = useState(type || '');
  const [searchEverywhere, setSearchEverywhere] = useState(globalSearch);
  const [localSearch, setLocalSearch] = useState(null);
  const [localSearching, setLocalSearching] = useState(false);
  const [localError, setLocalError] = useState('');
  const attemptedAutoLoadRef = useRef('');

  useEffect(() => { setQuery(initialQuery || ''); }, [initialQuery]);
  useEffect(() => { setSelectedType(type || ''); }, [type]);
  useEffect(() => { setSearchEverywhere(globalSearch); }, [globalSearch]);

  const activeType = type === undefined ? selectedType : type;
  const isGlobalSearch = Boolean(globalSearch || searchEverywhere);
  const providerState = isGlobalSearch ? lifeOS?.search : lifeOS?.vault;
  const effectiveState = state || providerState || EMPTY_VAULT_STATE;
  const response = data !== undefined ? data : effectiveState.data;

  useEffect(() => {
    if (!autoLoad || isGlobalSearch || data !== undefined || state?.data || !lifeOS || effectiveState.loading) return;
    const key = `${activeType || ''}:${category || ''}:${projectId || ''}`;
    if (attemptedAutoLoadRef.current !== key) {
      attemptedAutoLoadRef.current = key;
      void lifeOS.loadVault({ type: activeType || undefined, category, projectId });
    }
  }, [activeType, autoLoad, category, data, effectiveState.data, effectiveState.loading, isGlobalSearch, lifeOS, projectId, state?.data]);

  const shownResponse = localSearch || response;
  const records = useMemo(() => (
    isGlobalSearch
      ? resultItems(shownResponse, data === undefined ? effectiveState : null)
      : vaultItems(shownResponse, data === undefined ? effectiveState : null)
  ), [data, effectiveState, isGlobalSearch, shownResponse]);
  const loading = data !== undefined ? false : Boolean(effectiveState.loading || localSearching);
  const error = data !== undefined ? '' : (localError || effectiveState.error);
  const visibleItems = records.slice(0, Math.max(1, Number(maxVisibleItems) || 24));
  const hasMore = !isGlobalSearch && Boolean(effectiveState.hasMore || response?.next_cursor || response?.nextCursor);
  const semanticSearch = shownResponse?.semantic_search || shownResponse?.semanticSearch || effectiveState.semanticSearch;

  async function submitSearch(event) {
    event.preventDefault();
    const trimmed = query.trim();
    setLocalError('');
    setLocalSearch(null);
    if (!trimmed) {
      if (!isGlobalSearch) {
        if (onRefresh) void onRefresh();
        else void lifeOS?.loadVault({ type: activeType || undefined, category, projectId });
      }
      return;
    }
    const handler = onSearch || (isGlobalSearch
      ? (options => lifeOS?.searchLifeOS(options))
      : (options => lifeOS?.loadVault(options)));
    if (!handler) return;
    setLocalSearching(true);
    try {
      const result = await handler(isGlobalSearch
        ? { query: trimmed }
        : { query: trimmed, type: activeType || undefined, category, projectId });
      if (onSearch) setLocalSearch(result || null);
    } catch (cause) {
      setLocalError(cause?.response?.data?.error || cause?.response?.data?.detail || 'Não foi possível pesquisar agora.');
    } finally {
      setLocalSearching(false);
    }
  }

  function refresh() {
    if (onRefresh) {
      void onRefresh();
      return;
    }
    if (isGlobalSearch && query.trim()) {
      void lifeOS?.searchLifeOS({ query: query.trim() });
      return;
    }
    void lifeOS?.loadVault({ query: query.trim() || undefined, type: activeType || undefined, category, projectId });
  }

  function applyFilter(nextType) {
    setLocalSearch(null);
    setQuery('');
    setSelectedType(nextType || '');
    onFilterChange?.(nextType || '');
    if (!lifeOS || isGlobalSearch) return;
    attemptedAutoLoadRef.current = `${nextType || ''}:${category || ''}:${projectId || ''}`;
    void lifeOS.loadVault({ type: nextType || undefined, category, projectId });
  }

  function loadMore() {
    if (onLoadMore) {
      void onLoadMore();
      return;
    }
    void lifeOS?.loadVault({ ...(effectiveState.params || {}), append: true });
  }

  function toggleSearchScope() {
    if (globalSearch) return;
    setSearchEverywhere(previous => !previous);
    setLocalSearch(null);
    setLocalError('');
    setQuery('');
  }

  return (
    <section className={lifeOSClassNames('life-os-panel', 'life-os-vault', className)} aria-labelledby={panelId}>
      <header className="life-os-panel__header">
        <div>
          <p className="life-os-eyebrow">ATHENA VAULT</p>
          <h2 id={panelId}>{title}</h2>
          {isGlobalSearch && <p className="life-os-panel__subheading">Busca em projetos, tarefas, arquivos e itens salvos</p>}
        </div>
        <div className="life-os-panel__tools">
          {onAddItem && <button type="button" className="life-os-button life-os-button--quiet" onClick={onAddItem}>Salvar item</button>}
          {!globalSearch && <button type="button" className="life-os-button life-os-button--quiet" onClick={toggleSearchScope}>{isGlobalSearch ? 'Ver Vault' : 'Buscar tudo'}</button>}
          <button type="button" className="life-os-icon-button" onClick={refresh} disabled={loading} aria-label="Atualizar Vault" title="Atualizar">↻</button>
        </div>
      </header>

      <form className="life-os-search" onSubmit={submitSearch} role="search">
        <label htmlFor={`${panelId}-query`} className="life-os-sr-only">Buscar no {isGlobalSearch ? 'Life OS' : 'Vault'}</label>
        <input
          id={`${panelId}-query`}
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={isGlobalSearch ? 'Buscar em tudo…' : 'Buscar no Vault…'}
          maxLength={240}
        />
        <button type="submit" className="life-os-button" disabled={loading || !query.trim()}>
          Buscar
        </button>
      </form>

      {!isGlobalSearch && Array.isArray(filters) && filters.length > 0 && (
        <div className="life-os-filter-row" aria-label="Filtrar tipos do Vault">
          {filters.map(filter => {
            const id = typeof filter === 'string' ? filter : filter?.id;
            const label = typeof filter === 'string' ? lifeOSTypeLabel(filter) : filter?.label;
            const active = (id || '') === (activeType || '');
            return (
              <button
                type="button"
                key={id || 'all'}
                className={lifeOSClassNames('life-os-filter', active && 'is-active')}
                onClick={() => applyFilter(id)}
                aria-pressed={active}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {semanticSearch && semanticSearch.available === false && (
        <p className="life-os-search-note" role="status">Busca lexical</p>
      )}

      {error && (
        <div className="life-os-notice life-os-notice--error" role="alert">
          <span>{error}</span>
          <button type="button" className="life-os-link-button" onClick={refresh}>Tentar novamente</button>
        </div>
      )}

      {loading && records.length === 0 ? <VaultLoading /> : (
        <>
          {visibleItems.length > 0 && (
            <ul className="life-os-vault-grid" aria-label={isGlobalSearch ? 'Resultados da busca' : 'Itens do Vault'}>
              {visibleItems.map((item, index) => <VaultCard item={item} onOpenItem={onOpenItem} key={item?.id || `${lifeOSItemTitle(item)}-${index}`} />)}
            </ul>
          )}
          {!loading && !error && records.length === 0 && (
            <div className="life-os-empty" role="status"><p>{query.trim() ? 'Nenhum item encontrado.' : emptyMessage}</p></div>
          )}
          {hasMore && <button type="button" className="life-os-button life-os-button--quiet life-os-show-more" onClick={loadMore} disabled={loading}>{loading ? 'Carregando…' : 'Carregar mais'}</button>}
        </>
      )}
    </section>
  );
}

export default VaultPanel;
